/**
 * The info panel unit and building selections share (mockup 02), on
 * DockPanel, with four slots: the header (badge, name, level or rank chip,
 * city / group chips, hp), the task line (a dot coloured fight / work / idle
 * and the current task), the stats column (`stats`: the stat grid, output,
 * roster) and the Upgrades applied + Next column (`applied`, `next`).
 * `UnitInfoPanel` and `BuildingPanel` fill them from `view/unitInfo.ts` and
 * `view/buildingInfo.ts`; nothing here knows a rule.
 */
import type { ReactNode } from 'react';
import type { AppliedRow, TaskTone } from '@/game/view/unitInfo';
import { DockPanel } from './hud/DockPanel';

const DOT: Record<TaskTone, string> = {
  fight: 'bg-danger ring-[3px] ring-danger/20',
  work: 'bg-ok ring-[3px] ring-ok/20',
  idle: 'bg-fg-subtle',
};

export interface InfoPanelProps {
  /** What it is ("unit", "building"): names the close button. */
  kind: string;
  /** Glyph or tag in the round badge, e.g. "Wr" or "🌾". */
  badge: string;
  /** Rank chevrons drawn on the badge corner. */
  badgeMark?: string;
  name: string;
  /** Shown in place of `name` in the header, e.g. a custom name or its rename input. */
  nameNode?: ReactNode;
  /** Rank or level chip. */
  level?: ReactNode;
  /** Home city, group, shelter, hp as chips. */
  chips?: ReactNode;
  /** hp bar under the header; null for none. */
  hp?: { hp: number; max: number } | null;
  /** The task line: dot tone and its text; null for none (a rival's unit). */
  task?: { tone: TaskTone; text: ReactNode } | null;
  /** Right end of the task line: buttons that act on the task, or a future upgrade timer. */
  taskSlot?: ReactNode;
  /** Left column: the stat grid, output, roster. */
  stats: ReactNode;
  /** Right column, "Upgrades applied". */
  applied: readonly AppliedRow[];
  /** Right column, "Next" rows. */
  next: ReactNode;
  footer?: ReactNode;
  onLocate?: () => void;
  onClose: () => void;
}

export function InfoPanel({
  kind,
  badge,
  badgeMark,
  name,
  nameNode,
  level,
  chips,
  hp,
  task,
  taskSlot,
  stats,
  applied,
  next,
  footer,
  onLocate,
  onClose,
}: InfoPanelProps) {
  return (
    <DockPanel
      size="wide"
      kind={kind}
      title={
        <span className="flex items-center gap-2">
          <span className="relative grid h-7 w-7 shrink-0 place-items-center rounded-xl bg-fill text-[11px] font-medium">
            {badge}
            {badgeMark ? (
              <span className="absolute bottom-0 right-0.5 text-[7px] text-warn">{badgeMark}</span>
            ) : null}
          </span>
          <span className="truncate">{nameNode ?? name}</span>
        </span>
      }
      level={level}
      chips={chips}
      onLocate={onLocate}
      onClose={onClose}
      toolbar={
        <>
          {hp ? (
            <div
              role="progressbar"
              aria-label={`${name} hp`}
              aria-valuemin={0}
              aria-valuemax={hp.max}
              aria-valuenow={hp.hp}
              className="h-1 shrink-0 overflow-hidden rounded-full bg-fill"
            >
              <div
                className="h-full rounded-full bg-ok"
                style={{ width: `${Math.round(Math.min(1, hp.hp / Math.max(1, hp.max)) * 100)}%` }}
              />
            </div>
          ) : null}
          {task ? (
            <div className="flex shrink-0 items-center gap-2 rounded-xl bg-fill px-2.5 py-1">
              <span aria-hidden className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT[task.tone]}`} />
              <span role="status" className="min-w-0 flex-1 truncate tabular-nums" data-slot="task">
                {task.text}
              </span>
              {/* Task buttons (none yet: the sim has no stop order) or, for a building, the future owner-only upgrade timer. */}
              {taskSlot}
            </div>
          ) : null}
        </>
      }
      footer={footer}
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="flex min-w-0 flex-col gap-1.5">{stats}</div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <SectionHead>Upgrades applied</SectionHead>
          <AppliedList rows={applied} />
          <SectionHead>Next</SectionHead>
          {next}
        </div>
      </div>
    </DockPanel>
  );
}

export function SectionHead({ children }: { children: ReactNode }) {
  return (
    <h4 className="text-[10px] font-normal uppercase tracking-wider text-fg-subtle">{children}</h4>
  );
}

export function AppliedList({ rows }: { rows: readonly AppliedRow[] }) {
  if (rows.length === 0) return <p className="text-fg-subtle">Nothing applied yet</p>;
  return (
    <ul className="flex flex-col gap-0.5">
      {rows.map((r) => (
        <li
          key={`${r.src}${r.label}${r.effect}`}
          className="grid grid-cols-[1.25rem_minmax(0,1fr)_auto] items-baseline gap-1 text-xs"
        >
          <span aria-hidden className="text-fg-subtle">
            {r.src}
          </span>
          <span className="truncate text-fg-muted">{r.label}</span>
          <b className="font-medium text-ok">{r.effect}</b>
        </li>
      ))}
    </ul>
  );
}

/** One "Next" row: glyph, what, the button or a plain note; why-not on a line of its own. */
export function NextRow({
  glyph,
  title,
  note,
  noteTone,
  action,
}: {
  glyph: string;
  title: ReactNode;
  note?: ReactNode;
  noteTone?: 'warn';
  action?: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[1.25rem_minmax(0,1fr)_auto] items-center gap-x-2 gap-y-0.5 rounded-lg bg-fill px-2 py-1">
      <span aria-hidden className="text-fg-subtle">
        {glyph}
      </span>
      <span className="min-w-0 truncate">{title}</span>
      {action ?? <span />}
      {note ? (
        <small
          className={`col-span-2 col-start-2 text-[11px] leading-tight ${noteTone === 'warn' ? 'text-warn' : 'text-fg-subtle'}`}
        >
          {note}
        </small>
      ) : null}
    </div>
  );
}
